import { useMemo } from 'react';
import classNames from 'classnames';

import { Button } from '../../button/button.component';
import { MinHeight } from '../../min-height/min-height';
import { SimpleText } from '../../simple-text/simple-text.component';
import { Text } from '../../text/text.component';
import { Breakpoints, getMediaQuery } from '../../../utils/breakpoints';
import { useMediaQuery } from '../../../utils/use-media-query';

import { UseCasesContentBlockProps } from './use-cases-content-block.config';

const UseCasesContentBlock = ({
  additionalClasses,
  button,
  ctaMinHeight,
  ctaRef,
  hasGradientBackground,
  hasMidBlueBackground,
  index = 0,
  isLast,
  text,
  textMinHeight,
  textRef,
  title,
  titleMinHeight,
  titleRef,
}: UseCasesContentBlockProps) => {
  const isMobile = useMediaQuery(getMediaQuery(Breakpoints.MOBILE, 'down'), false);
  const isTablet = useMediaQuery(getMediaQuery(Breakpoints.TABLET_PORTRAIT, 'down'), false);
  const darkBg = hasGradientBackground || hasMidBlueBackground;

  const styles = useMemo(
    () => ({
      container: {
        'snowflake-use-cases-content-block-content': true,
        'content-block-on-dark': darkBg,
      },
      title: {
        'snowflake-uses-cases-content-block-title': true,
        'text-color-text-04': hasMidBlueBackground,
        'text-color-text-04-ui-bg-03': hasGradientBackground,
      },
      text: {
        'text-color-text-04': hasMidBlueBackground,
        'text-color-text-04-ui-bg-03': hasGradientBackground,
        'text-size-regular': true,
      },
      cta: {
        'snowflake-button-blue': !darkBg,
        'snowflake-button-white': darkBg,
        'snowflake-button-link': isMobile,
        'snowflake-button-secondary': !isMobile,
        'snowflake-button-compact': true,
      },
    }),
    [darkBg, isMobile, hasMidBlueBackground, hasGradientBackground]
  );

  const showBorder = useMemo(() => {
    if (isMobile || isLast) {
      return false;
    }
    const divider = isTablet ? 2 : 4;
    return (index + 1) % divider !== 0;
  }, [isMobile, isTablet, index, isLast]);

  return (
    <div
      data-testid="snowflake-use-cases-content-block"
      className={classNames('snowflake-use-cases-content-block', additionalClasses)}
    >
      <div className={classNames(styles.container)} data-testid="snowflake-use-cases-content-block-content">
        <div className="snowflake-use-cases-text-content">
          <MinHeight
            data-testid="snowflake-use-cases-content-block-title"
            className={classNames(styles.title)}
            minHeight={titleMinHeight}
            childRef={titleRef}
          >
            {title ? <SimpleText {...title} /> : null}
          </MinHeight>
          <MinHeight
            data-testid="snowflake-use-cases-content-block-text"
            className={classNames(styles.text)}
            minHeight={textMinHeight}
            childRef={textRef}
          >
            {text ? <Text {...text} /> : null}
          </MinHeight>
        </div>
        <MinHeight
          data-testid="snowflake-use-cases-content-block-cta"
          className={classNames(styles.cta)}
          minHeight={ctaMinHeight}
          childRef={ctaRef}
        >
          {button ? <Button {...button} appliedCssClassNames={isMobile ? 'snowflake-button-link' : ''} /> : null}
        </MinHeight>

        {showBorder && (
          <div
            className={classNames('use-cases-content-block-vertical-divider', {
              'without-cta': !button,
            })}
          />
        )}
      </div>
    </div>
  );
};

export default UseCasesContentBlock;
